<script>
  /**
   * Scheduler jobs relevant to this tenant. Every job here runs for ALL active
   * tenants — none takes a tenant id — so the panel says so plainly.
   */
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';

  let { tenant } = $props();

  let loading = $state(true);
  let error = $state(null);
  let jobs = $state([]);
  let running = $state(null);

  async function load() {
    loading = true;
    error = null;
    try { jobs = await api.listTenantJobs(tenant.id); }
    catch (e) { error = e.message; }
    finally { loading = false; }
  }

  $effect(() => { tenant.id; tenant.is_active; load(); });

  async function run(job) {
    running = job.job_id;
    try {
      await api.runTenantJob(tenant.id, job.job_id);
      toast.success(`${job.label} started for all active tenants — results appear below when it finishes.`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      running = null;
    }
  }

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
</script>

<section class="space-y-4">
  <div>
    <h2 class="text-sm font-semibold">Jobs</h2>
    <p class="rounded-lg px-3 py-2 text-xs" style="background-color: var(--p-info-bg)">
      <i class="fas fa-circle-info" aria-hidden="true"></i>
      These jobs run for <strong>every active tenant</strong>, not just {tenant.name}. They also run on
      their own schedule; use this to avoid waiting for the next run (e.g. right after connecting Hostaway).
    </p>
  </div>

  {#if error}
    <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
  {:else if loading && !jobs.length}
    <p class="p-card p-4 text-sm p-muted">Loading…</p>
  {:else}
    <div class="p-card divide-y" style="border-color: var(--p-border)">
      {#each jobs as job (job.job_id)}
        <div class="flex flex-wrap items-start justify-between gap-4 px-4 py-3 text-sm">
          <div>
            <p class="font-medium">{job.label} <code class="text-xs p-faint">{job.job_id}</code></p>
            <p class="text-xs p-faint">{job.description}</p>
            <p class="mt-1 text-xs p-muted">
              This tenant: {job.tenant_last_run ? `${job.tenant_last_run.status} ${fmt(job.tenant_last_run.at)}` : 'no record'}
              · last run overall:
              {#if job.global_last_run}
                {job.global_last_run.success ? 'succeeded' : 'failed'} {fmt(job.global_last_run.end)}
              {:else}never{/if}
            </p>
            {#if !job.eligible && job.reason}
              <p class="mt-1 text-xs" style="color: var(--p-warning)">Won't include this tenant: {job.reason}</p>
            {/if}
            {#if job.recently_triggered}
              <p class="mt-1 text-xs p-muted">Triggered {fmt(job.recently_triggered)}, waiting to start…</p>
            {/if}
          </div>
          <button type="button" class="p-btn p-btn-quiet" onclick={() => run(job)}
                  disabled={!!running || !!job.recently_triggered || !tenant.is_active}>
            {running === job.job_id ? 'Starting…' : 'Run now'}
          </button>
        </div>
      {:else}
        <p class="px-4 py-6 text-center text-sm p-muted">No jobs available.</p>
      {/each}
    </div>
  {/if}
</section>
